import dayjs from 'dayjs';
import {useEffect, useState} from 'react';
import {generatePath, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useCreateTransaction} from '@/api/payments';
import {getGqlErrorMessage, isAccessRestrictionError} from '@/api/request';
import {FULL_DATE_FORMAT} from '@/constants';
import {BillingType, PaymentProcess, PaymentTransactionStatus} from '@/enums';
import {useSession, useTossPayWidgets} from '@/hooks';
import {useAirbridgeTracking} from '@/hooks/tracking';
import {PaymentsTranscation, ProductWithPriceInfo} from '@/interface';
import {paths} from '@/router';
import {Flexbox, FlexFullGrow, GrayRoundedBox, PaddingBox} from '@/styled-components';
import {palette, Z_INDEX} from '@/styles';
import {withComma} from '@/utils';
import {Button} from '../Button';
import toast from '../HotToast';
import {Modal} from '../Modal';
import {Typography, typographyStyle} from '../Typography';

interface IProps {
  item: ProductWithPriceInfo;
  onClose?(): void;
}

export const PayWidgetOrderSheet = ({item, onClose}: IProps) => {
  const {finalPrice} = item.priceInfo;
  const navigate = useNavigate();

  const {user} = useSession();
  const customerKey = user?.id;

  const {mutate: createTransaction, isPending} = useCreateTransaction();

  const {widgets, requestPayment} = useTossPayWidgets(customerKey);
  const [ready, setReady] = useState(false);

  const trackAirbridge = useAirbridgeTracking();

  const onClick = () => {
    if (!item || !customerKey) {
      return;
    }

    createTransaction(
      {
        customerKey,
        product: item.id,
        method: '카드', //  결제수단 확인 필요
      },
      {
        onSuccess: (data) => {
          const transaction = data.result;

          if (transaction?.status === PaymentTransactionStatus.PREPARE) {
            trackAirbridge('airbridge.initiateCheckout', {
              action: '구매',
              label: item.name,
            });
            onPay(transaction);
          } else {
            navigate(
              {
                pathname: generatePath(paths.payments_process, {
                  transaction,
                  status: PaymentProcess.fail,
                }),
                search: `message=트랜잭션 생성 실패&code=${
                  transaction
                    ? `${transaction.type}/${transaction.status}`
                    : 'Not found transaction.'
                }`,
              },
              {replace: true}
            );
          }
        },
        onError: (error) => {
          // 이용 제한은 공통 에러 핸들러가 안내 모달을 띄운다.
          if (!isAccessRestrictionError(error)) {
            toast.error(getGqlErrorMessage(error));
          }
        },
      }
    );
  };

  const onPay = async (transaction: PaymentsTranscation) => {
    if (!widgets) {
      return;
    }

    try {
      await requestPayment(transaction, item);
    } catch (error) {
      console.error(error);

      if (typeof (error as any)?.message === 'string') {
        toast.error((error as any).message);
      }
    }
  };

  const renderPaymentWidgets = async () => {
    if (!widgets || !finalPrice || ready) {
      return;
    }

    await widgets.setAmount({
      value: finalPrice,
      currency: 'KRW',
    });

    await Promise.all([
      widgets.renderPaymentMethods({
        selector: '#payment-method',
        variantKey: 'PAYMENT_MYFCHAMAP',
      }),
      widgets.renderAgreement({
        selector: '#agreement',
        variantKey: 'AGREEMENT_MYFCHAMAP',
      }),
    ]);

    setReady(true);
  };

  useEffect(() => {
    if (widgets) {
      renderPaymentWidgets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets]);

  return (
    <Modal
      title={item.name}
      width={800}
      zIndex={Z_INDEX.paymentModal}
      backdropColor={palette.gray100}
      onClose={onClose}
    >
      <Root>
        <PaddingBox spacing={[0, 24]}>
          <RoundedBox>
            {item.billingType === BillingType.Daily && !!item.value && (
              <Flexbox>
                <Typography fontWeight={700}>이용 기간</Typography>
                <FlexFullGrow />
                <Typography color={palette.gray600}>
                  {dayjs().format(FULL_DATE_FORMAT)}~
                  {dayjs().add(item.value, 'day').format(FULL_DATE_FORMAT)}
                </Typography>
              </Flexbox>
            )}
            <Flexbox>
              <Typography fontWeight={700}>총 결제 금액</Typography>
              <FlexFullGrow />
              <Typography color={palette.gray600}>
                {withComma(item.priceInfo.finalPrice)}원
              </Typography>
            </Flexbox>
          </RoundedBox>
        </PaddingBox>
        <div id="payment-method" />
        <div id="agreement" />
        <PaddingBox spacing={[0, 24]}>
          <Button
            size="medium"
            color="secondary"
            variant="fill"
            fullWidth
            disabled={!ready || isPending}
            onClick={onClick}
          >
            {isPending ? '결제 요청 중...' : '결제하기'}
          </Button>
        </PaddingBox>
      </Root>
    </Modal>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
`;

const RoundedBox = styled(GrayRoundedBox)`
  display: flex;
  flex-direction: column;
  gap: 16px 12px;
  ${{...typographyStyle.medium14}}
`;
